// Shared trigger for the "Investigate online …" typed shortcut. When a chat
// message starts with this phrase, both hosts (the /octopus page and the FAB
// popover) route it to the web-discovery / graph-expansion flow instead of a
// normal chat turn — behaving exactly like the graph node's "Investigate
// online" button. Single source of truth so the two hosts never drift.
export const isInvestigateOnlinePrompt = (content: string): boolean =>
  // Match "investigate online this seller", "investigate this seller online",
  // "investigate <name> online", etc. — any request that starts with
  // "investigate" and mentions "online" on the same line (a trailing
  // "References:" block sits on later lines, so it's excluded).
  /^\s*investigate\b[^\n]*\bonline\b/i.test(content)

export const parseEntityQuery = (query: string): { type: 'item' | 'seller' | 'saw' | null; id: string | null } => {
  const lowerQuery = query.toLowerCase()

  // Pattern for prefixed IDs (seller_uuid, item_uuid, saw_uuid)
  const prefixedIdRegex = /(seller|item|saw)_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
  const prefixedMatch = query.match(prefixedIdRegex)

  if (prefixedMatch) {
    const prefix = prefixedMatch[1].toLowerCase() as 'seller' | 'item' | 'saw'
    const fullId = prefixedMatch[0]
    return { type: prefix, id: fullId }
  }

  // Plain UUID pattern (no prefix)
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  const uuidMatch = query.match(uuidRegex)

  if (lowerQuery.includes('seller') || lowerQuery.includes('vendor')) {
    return { type: 'seller', id: uuidMatch ? `seller_${uuidMatch[0]}` : null }
  }
  if (lowerQuery.includes('item') || lowerQuery.includes('product') || lowerQuery.includes('listing')) {
    return { type: 'item', id: uuidMatch ? `item_${uuidMatch[0]}` : null }
  }
  if (lowerQuery.includes('saw') || lowerQuery.includes('domain') || lowerQuery.includes('website')) {
    return { type: 'saw', id: uuidMatch ? `saw_${uuidMatch[0]}` : null }
  }

  if (uuidMatch) {
    return { type: 'seller', id: `seller_${uuidMatch[0]}` }
  }

  return { type: null, id: null }
}

// Shared trigger for the typed "expand <entity>" shortcut — routes to the
// node-expansion action (identical to the graph's Expand button) instead of a
// chat turn. Both hosts (page + FAB) use this so they never drift.
export const isExpandPrompt = (content: string): boolean =>
  /^\s*expand\b/i.test(content)

// Resolve which node an "expand X" message targets, matching against the loaded
// graph: an explicit prefixed id / References entry, else a node by name, else a
// generic "this node / the graph / this seller" reference → the current root.
// Returns null when nothing resolves (e.g. "expand on that"), so the caller
// falls back to a normal chat turn.
export const resolveExpandTarget = (
  content: string,
  nodes?: Array<{ id: string; name?: unknown; title?: unknown; domain?: unknown }>,
  root?: string | null,
): string | null => {
  const q = parseEntityQuery(content)
  if (q.id) return q.id
  const rest = content.replace(/^\s*expand\s+/i, '').split('\n')[0].trim()
  const name = rest.replace(/^(the|this|that)\s+/i, '').trim().toLowerCase()
  const norm = (v: unknown) =>
    (typeof v === 'string' ? v : Array.isArray(v) ? String(v[0] ?? '') : '').toLowerCase().trim()
  if (name && nodes?.length) {
    const exact = nodes.find((n) => [n.name, n.title, n.domain].some((v) => norm(v) === name))
    if (exact) return exact.id
    const partial = nodes.find((n) =>
      [n.name, n.title, n.domain].some((v) => norm(v).length > 2 && (norm(v).includes(name) || name.includes(norm(v)))),
    )
    if (partial) return partial.id
  }
  // Generic "expand this node / the graph / this seller" → current root node.
  if (root && /^(this|that|the|current|it|my|node|graph|network|seller|entity|item|site)\b/i.test(rest)) return root
  return null
}

// ── Discovery relation subcategories ────────────────────────────────────────
// `discovery` edges (web-presence results) all share one edge label but carry a
// `source_kind` telling you *what kind* of link it is. The relation filter folds
// that subcategory into the filter key (`discovery:<kind>`) so "Discovered"
// breaks down into "Discovered · social post", "Discovered · seller", etc.
// Single source of truth shared by both filter hosts and the edge renderer, so
// label/colour never drift. Server enum is closed; unknowns fall back to `other`.

export const DISCOVERY_KIND_LABELS: Record<string, string> = {
  social_profile: 'social profile',
  social_post: 'social post',
  marketplace_seller: 'seller',
  marketplace_listing: 'listing',
  official_site: 'official site',
  news_article: 'news',
  other: 'link',
}

export const DISCOVERY_KIND_COLORS: Record<string, string> = {
  social_profile: '#ec4899', // pink-500
  social_post: '#f472b6', // pink-400
  marketplace_seller: '#f59e0b', // amber-500
  marketplace_listing: '#d97706', // amber-600
  official_site: '#059669', // emerald-600
  news_article: '#0ea5e9', // sky-500
  other: '#64748b', // slate-500
}

// Normalise a raw source_kind to a known key (older data used bare 'marketplace').
export const normalizeDiscoveryKind = (kind?: string): string =>
  kind === 'marketplace' ? 'marketplace_seller' : kind && kind in DISCOVERY_KIND_LABELS ? kind : 'other'

// The key the relation filter groups/toggles an edge by. For discovery edges
// this is `discovery:<kind>`; for every other edge it's just the edge label.
export const edgeFilterKey = (e: { label?: string; source_kind?: unknown } | undefined | null): string => {
  if (!e?.label) return ''
  if (e.label === 'discovery') {
    return `discovery:${normalizeDiscoveryKind(typeof e.source_kind === 'string' ? e.source_kind : undefined)}`
  }
  return e.label
}

// Stable identity for an edge: endpoints + relation + discovery subcategory.
// Graphs (both live-merged and persisted network_context) can accumulate exact
// duplicate edges — many carry no `id`, so id-keyed merges let dupes through.
// Duplicates collide on React/ReactFlow keys (edges are keyed `from->to-label`),
// which breaks reconciliation: toggling a relation filter then leaves stale edge
// DOM behind ("nodes update, lines don't"). Dedupe on this key before render.
export const edgeDedupeKey = (e: { from_id?: unknown; to_id?: unknown; label?: unknown; source_kind?: unknown }): string =>
  `${String(e?.from_id ?? '')}->${String(e?.to_id ?? '')}:${String(e?.label ?? '')}:${typeof e?.source_kind === 'string' ? e.source_kind : ''}`

export const dedupeEdges = <T extends { from_id?: unknown; to_id?: unknown; label?: unknown; source_kind?: unknown }>(
  edges: T[],
): T[] => {
  const seen = new Set<string>()
  const out: T[] = []
  for (const e of edges) {
    const k = edgeDedupeKey(e)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(e)
  }
  return out
}

// Display label + colour for any relation filter key, handling `discovery:<kind>`.
export const describeEdgeFilterKey = (key: string): { label: string; color?: string } => {
  if (key.startsWith('discovery:')) {
    const kind = key.slice('discovery:'.length)
    return { label: `Discovered · ${DISCOVERY_KIND_LABELS[kind] ?? 'link'}`, color: DISCOVERY_KIND_COLORS[kind] }
  }
  return { label: '' } // non-discovery keys fall back to EdgeFilterPanel's own maps
}
